import React from 'react';
import styles from './ProgressBar.module.css';

interface ProgressBarProps {
  value: number;
  max?: number;
  /** CSS color for the fill — use a CSS variable. Defaults to --accent. */
  color?: string;
  /** Track height in px. Defaults to 5. */
  height?: number;
  /** Optional label rendered above the bar with value/max on the right. */
  label?: React.ReactNode;
  /** Show "N / max" counter on the right when label is set. Default true. */
  showValue?: boolean;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  color = 'var(--accent)',
  height = 5,
  label,
  showValue = true,
  className,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(value, max));
  const pct = max > 0 ? (clamped / max) * 100 : 0;

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {label && (
        <div className={styles.labelRow}>
          <span className={styles.label}>{label}</span>
          {showValue && (
            <span className={styles.value}>
              {clamped}
              {max !== 100 && ` / ${max}`}
            </span>
          )}
        </div>
      )}
      <div
        className={styles.track}
        style={{ height }}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div className={styles.fill} style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
